<template>
    <div :class="containerClass">
        <DsSpinner v-if="loading" />

        <DsStack v-else space="spacing-200">
            <DsInline spread>
                <DsListItem next class="list-item">
                    <template #description>
                        <DsStack>
                            <DsText>{{ partner.phone }}</DsText>

                            <DsText>{{ partner.email }}</DsText>
                        </DsStack>
                    </template>

                    <template #overline>
                        {{ $t('partnerCompanyInfo.keapCertifiedPartner') }}
                    </template>

                    <template #leading>
                        <DsAvatar :image-url="partner.logoUrl" />
                    </template>

                    <template #title>
                        {{ partner.name }}
                    </template>
                </DsListItem>

                <DsIcon class="arrow-icon" name="arrow-right" />

                <DsListItem next selected class="list-item">
                    <template #description>
                        {{ $t('partnerCompanyInfo.appId') }}: {{ selectedApp.appId }}
                    </template>

                    <template #overline>
                        {{ $t('partnerCompanyInfo.yourKeapApp') }}
                    </template>

                    <template #leading>
                        <DsAvatar image-url="https://i.imgur.com/bDXB7Rj.png" />
                    </template>

                    <template #title>
                        {{ selectedApp.title }}
                    </template>

                    <template #trailing>
                        <DsDropdown position="bottom-end" variant="search">
                            <template #default="{ attrs }">
                                <DsActionsContainer>
                                    <DsIconButton v-bind="attrs" name="more-vertical" />
                                </DsActionsContainer>
                            </template>

                            <template #content>
                                <DsMenuSearchHeader v-model:search="search" />

                                <DsMenuItems v-if="filteredItems.length > 0">
                                    <DsMenuItem
                                        v-for="item in filteredItems"
                                        :key="item.appId"
                                        @click="selectApp(item)"
                                    >
                                        <DsStack>
                                            <DsText>{{ item.title }}</DsText>
                                            <DsText variant="body-2">
                                                {{ $t('partnerCompanyInfo.appId') }}:
                                                {{ item.appId }}
                                            </DsText>
                                        </DsStack>
                                    </DsMenuItem>
                                </DsMenuItems>

                                <DsMenuEmpty v-else>
                                    {{ $t('partnerCompanyInfo.noResults') }}
                                </DsMenuEmpty>
                            </template>
                        </DsDropdown>
                    </template>
                </DsListItem>
            </DsInline>

            <DsBox>
                <DsText variant="body">
                    <DsInline align-y="center" space="spacing-100">
                        <DsSpinner v-if="loading" size="sm" />
                        <span v-if="loading">{{
                            $t('partnerCompanyInfo.loadingPartnerInformation')
                        }}</span>
                        <span v-else>{{
                            $t('partnerCompanyInfo.grantPartnerAccess', {
                                partnerName: partner.name,
                            })
                        }}</span>
                    </DsInline>
                </DsText>
            </DsBox>

            <DsBox padding-bottom="spacing-100">
                <DsInline>
                    <DsButton class="grant-access">
                        {{ $t('partnerCompanyInfo.grantAccess') }}
                    </DsButton>
                    <DsTextButton @click="showDeclineModal = true">
                        {{ $t('partnerCompanyInfo.cancel') }}
                    </DsTextButton>
                </DsInline>
            </DsBox>

            <AccordionPanel>
                <template #header>
                    <DsText>{{ $t('partnerCompanyInfo.advancedSettings') }}</DsText>
                </template>

                <template #content>
                    <DsBox padding="spacing-200">
                        <DsStack space="spacing-100">
                            <DsText variant="headline-3">
                                {{ $t('partnerCompanyInfo.assignRole') }}
                            </DsText>

                            <DsText variant="body-2">
                                {{ $t('partnerCompanyInfo.assignRoleDescription') }}
                            </DsText>

                            <DsDropdown position="bottom-end">
                                <template #default>
                                    <DsMultiselect
                                        v-model="value"
                                        label-prop="name"
                                        value-prop="id"
                                        :placeholder="
                                            $t('partnerCompanyInfo.assignRolePlaceholder')
                                        "
                                        searchable
                                        :options="options"
                                        required
                                        pin-bottom
                                        :submitted="submitted"
                                    />
                                </template>

                                <template #content>
                                    <DsMenuItems>
                                        <DsMenuItem>
                                            {{ $t('partnerCompanyInfo.switchApp') }}
                                        </DsMenuItem>
                                    </DsMenuItems>
                                </template>
                            </DsDropdown>

                            <DsInlineAlert :leading-icon="value.leading" :type="value.type">
                                {{ value.alertText }}
                            </DsInlineAlert>
                        </DsStack>
                    </DsBox>
                </template>
            </AccordionPanel>

            <DeclineFeedbackModal :is-open="showDeclineModal" @close="showDeclineModal = false" />
        </DsStack>
    </div>
</template>

<script>
import { AccordionPanel } from '@keap-web/shared-ui';
import DeclineFeedbackModal from './DeclineFeedbackModal.vue';

export default {
    name: 'PartnerCompanyInfo',
    components: {
        AccordionPanel,
        DeclineFeedbackModal,
    },
    props: {
        partnerId: { type: [String, Number], default: null },
    },
    emits: ['access-declined', 'app-selected'],
    data() {
        return {
            search: '',
            showDeclineModal: false,
            selectedApp: {
                title: 'BlueWave Plumbing Services - Pacific Northwest',
                appId: 'qr540',
            },
            submitted: false,
            loading: true,
            error: '',
            partner: {
                name: 'Crucial Consulting',
                email: 'support@crucialconsulting.com',
                phone: '+1 (602) 555-5555',
                website: 'https://crucialconsulting.com',
                logoUrl: 'https://via.placeholder.com/64/EEF3F7/1E2A32?text=CC',
            },
            logoFailed: false,
            options: [
                {
                    id: 'admin',
                    name: 'Admin',
                    leading: 'alert-triangle',
                    type: 'warning',
                    alertText:
                        'This role gives full account access, including creating, editing and deleting anything in this Keap app. Assign only to trusted people.',
                },
                {
                    id: 'limitedAdmin',
                    name: 'Limited Admin',
                    leading: 'alert-triangle',
                    type: 'warning',
                    alertText:
                        'This role has full account permissions except the ability to create other roles. This includes the ability to create, edit and delete anything in this Keap app. Only assign this role to trusted people.',
                },
                {
                    id: 'Staff',
                    name: 'Staff',
                    leading: 'alert-circle',
                    type: 'subtle',
                    alertText: 'This role is for general staff members with basic access.',
                },
                {
                    id: 'Manager',
                    name: 'Manager',
                    leading: 'alert-circle',
                    type: 'subtle',
                    alertText: 'This role is for managers with elevated permissions.',
                },
                {
                    id: 'customRole',
                    name: 'Custom Role',
                    leading: 'alert-triangle',
                    type: 'warning',
                    alertText: 'This role has custom permissions defined by the admin.',
                },
            ],
            value: {
                id: 'admin',
                name: 'Admin',
                leading: 'alert-triangle',
                type: 'warning',
                alertText:
                    'This role gives full account access, including creating, editing and deleting anything in this Keap app. Assign only to trusted people.',
            },
        };
    },
    computed: {
        items() {
            return [
                {
                    title: 'BlueWave Plumbing Services - Pacific Northwest',
                    appId: 'qr540',
                },
                {
                    title: 'BlueWave Plumbing Services - Southern California',
                    appId: 'ab123',
                },
                {
                    title: 'BlueWave Plumbing Services - Great Lakes Region',
                    appId: 'cd456',
                },
                {
                    title: 'BlueWave Plumbing Services - Northeast Corridor',
                    appId: 'ef789',
                },
            ];
        },
        filteredItems() {
            if (!this.search) {
                return this.items;
            }

            return this.items.filter((item) => {
                return (
                    item.title.toLowerCase().includes(this.search.toLowerCase())
                    || item.appId.toLowerCase().includes(this.search.toLowerCase())
                );
            });
        },
        initials() {
            if (!this.partner.name) return 'KP';

            return this.partner.name
                .split(/\s+/)
                .map((p) => p[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();
        },
        containerClass() {
            return this.loading ? 'component-container' : '';
        },
    },
    mounted() {
        this.fetchPartner();
    },

    methods: {
        submit() {
            this.submitted = true;

            if (this.$refs.form.checkValidity()) {
                this.$toast({
                    message: this.$t('partnerCompanyInfo.submitSuccessful'),
                });
            }
        },

        async fetchPartner() {
            try {
                this.loading = true;
                const data = await new Promise((resolve) => {
                    setTimeout(() => {
                        resolve({
                            id: this.partnerId || 'demo-123',
                            name: 'Crucial Consulting',
                            email: 'support@crucialconsulting.com',
                            phone: '+1 (602) 555-5555',
                            website: 'https://crucialconsulting.com',
                            logoUrl: 'https://via.placeholder.com/64/EEF3F7/1E2A32?text=CC',
                        });
                    }, 300);
                });

                this.partner = data;
                this.error = '';
            } catch (e) {
                this.error = this.$t('partnerCompanyInfo.unableToLoadPartnerInfo');
            } finally {
                this.loading = false;
            }
        },
        onLogoError() {
            this.partner.logoUrl = '';
            this.logoFailed = true;
        },

        selectApp(app) {
            this.selectedApp = { ...app };
            this.$emit('app-selected', app);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../styles/main.scss';

.list-item {
    width: px-to-rem(360);
    box-shadow: $elevation-z0;
    border-radius: px-to-rem(8);
}

.arrow-icon {
    align-self: center;
}

.grant-access {
    margin: 0 $spacing-200 $spacing-200 0;
}

.component-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: px-to-rem(200px);
}
</style>

<i18n>
{
    "en": {
        "partnerCompanyInfo": {
            "keapCertifiedPartner": "Keap Certified Partner",
            "yourKeapApp": "Your Keap App",
            "appId": "App ID",
            "noResults": "There are no results matching your search.",
            "advancedSettings": "Advanced Settings",
            "assignRole": "Assign Role",
            "allCrucialConsultingTeamMembers": "All Crucial Consulting team members will have the permissions tied to this role. Consider checking with the Keap Certified Partner before making changes.",
            "assignRoleDescription": "All Crucial Consulting team members will have the permissions tied to this role. Consider checking with the Keap Certified Partner before making changes.",
            "assignRolePlaceholder": "Assign a role to this Partner",
            "switchApp": "Switch App",
            "loadingPartnerInformation": "Loading partner information...",
            "grantPartnerAccess": "Grant {partnerName} access to your Keap app?",
            "grantAccess": "Grant Access",
            "cancel": "Cancel",
            "submitSuccessful": "Submit successful",
            "unableToLoadPartnerInfo": "Unable to load partner information."
        }
    }
}
</i18n>
